import _cloneDeep from 'lodash/cloneDeep';

import { REWARD_MODE_TIER_BASED } from '../rewardMode';
import { IReferralTier, IReferralTierDisplay } from './types';

export const TIER_LOCALES = ['en', 'th', 'de', 'fr', 'it'] as const;

interface ITierRulePayload {
  name?: string;
  event_name?: string;
  conditions?: any[];
}

interface ITierOutcomePayload {
  type?: string;
  id?: number | string;
}

interface ITierPayload {
  id?: number | string;
  required_referral_count?: number;
  rule?: ITierRulePayload;
  // A tier may carry either flat outcomes `{type, id}` or prize-set wrappers
  // `{type: 'prize_set' | 'randomized_outcome_set', outcomes: [...], ...}` —
  // matching the shape other campaign types send.
  outcomes?: any[];
  [key: string]: any;
}

const toRulePayload = (tier: IReferralTier): ITierRulePayload | undefined => {
  const source = tier?.rules?.[0] ?? tier?.rule;
  if (!source) return undefined;
  return {
    ...(source.id !== undefined ? { id: source.id } : {}),
    name: source.name,
    event_name: (source as any).event_name,
    conditions: Array.isArray((source as any).conditions) ? (source as any).conditions : []
  };
};

const PRIZE_SET_TYPES = new Set(['prize_set', 'randomized_outcome_set']);

const TIER_OUTCOME_PASSTHROUGH = [
  'outcome_value',
  'points_count',
  'expiry_period',
  'expiry_unit',
  'expiry_fixed_date',
  'expiry_use_campaign_end_date',
  'campaign_options'
];

const toTierOutcomePayload = (outcome: any): ITierOutcomePayload => {
  const type = outcome?.type ?? outcome?.item_type;
  const id = outcome?.id ?? outcome?.item_id;
  const payload: ITierOutcomePayload = { type, id };
  for (const key of TIER_OUTCOME_PASSTHROUGH) {
    if (outcome?.[key] !== undefined && outcome[key] !== null) {
      (payload as any)[key] = outcome[key];
    }
  }
  return payload;
};

const toPrizeSetChildPayload = (child: any, parentSetId?: number | string): any | null => {
  const itemType = child?.item_type ?? child?.type;
  if (!itemType) return null;
  const isCustom = itemType === 'custom';
  const itemId = child?.item_id ?? child?.id;
  if (!isCustom && (itemId === undefined || itemId === null)) return null;
  const payload: any = { item_type: itemType };
  if (itemId !== undefined && itemId !== null) payload.item_id = itemId;
  if (child?.id !== undefined && child?.item_id !== undefined && child.id !== child.item_id) {
    payload.id = child.id;
  }
  const resolvedSetId = child?.prize_set_id ?? parentSetId;
  if (payload.id !== undefined && resolvedSetId !== undefined && resolvedSetId !== null) {
    payload.prize_set_id = resolvedSetId;
  }
  if (isCustom && child?.identifier) payload.identifier = child.identifier;
  // Pass through optional per-child config the BE understands.
  ['points_count', 'outcome_value', 'probability', 'score'].forEach(k => {
    if (child?.[k] !== undefined && child[k] !== null) payload[k] = child[k];
  });
  return payload;
};

const collectTierOutcomes = (tierOutcomes: any[]): any[] => {
  const out: any[] = [];
  for (const o of tierOutcomes) {
    const t = o?.type ?? o?.item_type;
    if (t && PRIZE_SET_TYPES.has(t)) {
      const children = Array.isArray(o?.outcomes) ? o.outcomes : [];
      const parentSetId = t === 'prize_set' ? o?.id : undefined;
      const childPayloads = children
        .map((c: any) => toPrizeSetChildPayload(c, parentSetId))
        .filter(Boolean);
      if (childPayloads.length === 0) continue;
      const setPayload: any = {
        type: t,
        ...(o?.id !== undefined ? { id: o.id } : {}),
        ...(o?.name ? { name: o.name } : {}),
        ...(o?.new_api !== undefined ? { new_api: o.new_api } : {}),
        ...(o?.issue_mode !== undefined ? { issue_mode: o.issue_mode } : {}),
        ...(o?.issues_count !== undefined ? { issues_count: o.issues_count } : {}),
        outcomes: childPayloads
      };
      out.push(setPayload);
      continue;
    }
    const payload = toTierOutcomePayload(o);
    if (payload.type && payload.id) out.push(payload);
  }
  return out;
};

const DISPLAY_FIELD_PREFIXES = ['title_', 'subtitle_', 'icon_url_'] as const;

export const toIconUrlString = (val: any): string | undefined => {
  if (val === undefined || val === null || val === '') return undefined;
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    const inner = val.value ?? val;
    return inner?.image_url || inner?.url || inner?.file || undefined;
  }
  return undefined;
};

const flattenDisplay = (display: IReferralTierDisplay | undefined) => {
  const out: Record<string, any> = {};
  if (!display) return out;
  Object.keys(display).forEach(key => {
    const rawValue = (display as any)[key];
    if (rawValue === undefined || rawValue === null || rawValue === '') return;
    const prefix = DISPLAY_FIELD_PREFIXES.find(p => key.startsWith(p));
    if (!prefix) return;
    const locale = key.slice(prefix.length);
    if (!locale) return;
    const value = prefix === 'icon_url_' ? toIconUrlString(rawValue) : rawValue;
    if (value === undefined || value === '') return;
    out[`tier_${prefix}${locale}`] = value;
  });
  // Legacy single icon_url falls back to EN if no per-locale EN icon was set.
  if (display.icon_url && out.tier_icon_url_en === undefined) {
    const legacy = toIconUrlString(display.icon_url);
    if (legacy) out.tier_icon_url_en = legacy;
  }
  return out;
};

const toTierPayload = (tier: IReferralTier): ITierPayload => {
  const payload: ITierPayload = {
    ...((tier as any).id !== undefined ? { id: (tier as any).id } : {}),
    required_referral_count: tier?.threshold,
    ...flattenDisplay(tier?.display)
  };
  const rule = toRulePayload(tier);
  if (rule) payload.rule = rule;
  const tierOutcomes = Array.isArray((tier as any).outcomes)
    ? (tier as any).outcomes
    : tier?.outcome
    ? [tier.outcome]
    : [];
  payload.outcomes = collectTierOutcomes(tierOutcomes);
  return payload;
};

const CAMPAIGN_OUTCOME_PASSTHROUGH = [
  'total_reward_limit',
  'total_referree_reward_limit',
  'referee_required_for_reward',
  'count',
  'points_count',
  'outcome_value',
  'expiry_period',
  'expiry_unit',
  'expiry_fixed_date',
  'expiry_use_campaign_end_date',
  'rule_details',
  'gl_account_id',
  'gl_account_name',
  'gl_account_type'
];

const reshapeCampaignOutcome = (outcome: any): any | null => {
  const type = outcome?.type ?? outcome?.item_type;
  const id = outcome?.id ?? outcome?.item_id;
  if (!type || !id) return null;
  const payload: any = {
    type,
    id,
    award_to_referee: true,
    award_to_referral: false
  };
  for (const key of CAMPAIGN_OUTCOME_PASSTHROUGH) {
    if (outcome?.[key] !== undefined && outcome[key] !== null) payload[key] = outcome[key];
  }
  return payload;
};

const reshapeCampaignOutcomes = (outcomes: any): any[] => {
  if (!Array.isArray(outcomes) || outcomes.length === 0) return [];
  return outcomes.map(reshapeCampaignOutcome).filter(Boolean);
};

export const tierFormToPayload = (form: any): any => {
  if (!form || form.properties?.reward_mode !== REWARD_MODE_TIER_BASED) {
    return form;
  }
  const out = _cloneDeep(form);
  const ladder = out.properties?.tier_ladder;
  const tiers: IReferralTier[] = Array.isArray(ladder?.tiers) ? ladder.tiers : [];

  out.tier_based_invite = true;
  out.campaign_referral_type = 'user';
  out.tiers = tiers.map(toTierPayload);
  out.outcomes = reshapeCampaignOutcomes(out.outcomes);

  if (out.properties) {
    delete out.properties.reward_mode;
    delete out.properties.tier_ladder;
  }

  return out;
};

const WIRE_DISPLAY_PREFIXES = [
  { wire: 'tier_title_', form: 'title_' },
  { wire: 'tier_subtitle_', form: 'subtitle_' },
  { wire: 'tier_icon_url_', form: 'icon_url_' }
] as const;

const filenameFromUrl = (url: string): string => {
  try {
    const tail = url.split('?')[0].split('/').pop() || '';
    return tail || 'icon';
  } catch {
    return 'icon';
  }
};

const wrapIconForForm = (url: any): any => {
  if (url === undefined || url === null || url === '') return undefined;
  // Already an image object (defensive — round-trips that didn't hit the wire).
  if (typeof url === 'object') return url;
  if (typeof url !== 'string') return undefined;
  return {
    type: 'image',
    value: { filename: filenameFromUrl(url), image_url: url }
  };
};

const blueprintOutcomeTypeToWire = (modularizableType?: string): string | undefined => {
  if (!modularizableType) return undefined;
  switch (modularizableType) {
    case 'Reward::Campaign':
      return 'reward';
    case 'Campaign':
      return 'campaign';
    case 'LoyaltyProgram':
    case 'Loyalty':
    case 'StoredValue::Campaign':
      return 'points';
    case 'Badge':
      return 'badge';
    case 'PrizeSet':
      return 'prize_set';
    case 'RandomizedOutcomeSet':
      return 'randomized_outcome_set';
    default:
      return modularizableType.toLowerCase();
  }
};

const fromPrizeSetItem = (item: any): any => {
  const itemType = blueprintOutcomeTypeToWire(item?.item_type) ?? item?.item_type;
  return {
    ...(item?.id !== undefined ? { id: item.id } : {}),
    item_type: itemType,
    ...(item?.item_id !== undefined && item?.item_id !== null ? { item_id: item.item_id } : {}),
    ...(item?.prize_set_id !== undefined && item?.prize_set_id !== null
      ? { prize_set_id: item.prize_set_id }
      : {}),
    ...(item?.identifier ? { identifier: item.identifier } : {}),
    ...(item?.points_count != null ? { points_count: item.points_count } : {}),
    ...(item?.outcome_value != null ? { outcome_value: item.outcome_value } : {}),
    ...(item?.probability != null ? { probability: item.probability } : {})
  };
};

const fromTierOutcome = (o: any): any => {
  const type = blueprintOutcomeTypeToWire(o?.modularizable_type) ?? o?.outcome?.type ?? o?.type;
  if (type === 'prize_set' || type === 'randomized_outcome_set') {
    const items = Array.isArray(o?.outcome?.items)
      ? o.outcome.items
      : Array.isArray(o?.outcome?.outcomes)
      ? o.outcome.outcomes
      : Array.isArray(o?.outcomes)
      ? o.outcomes
      : [];
    const parentSrc = o?.outcome ?? o;
    return {
      type,
      ...(parentSrc?.id !== undefined ? { id: parentSrc.id } : {}),
      name: parentSrc?.name ?? o?.label,
      ...(parentSrc?.issue_mode !== undefined ? { issue_mode: parentSrc.issue_mode } : {}),
      ...(parentSrc?.issues_count !== undefined ? { issues_count: parentSrc.issues_count } : {}),
      new_api: true,
      outcomes: items.map(fromPrizeSetItem)
    };
  }

  const id = o?.modularizable_id ?? o?.outcome?.id ?? o?.id;
  const name = o?.outcome?.name ?? o?.label;
  const form: any = {
    _id: o?.id != null ? `to-${o.id}` : `to-${type}-${id}`,
    type,
    id
  };
  if (name !== undefined && name !== null && name !== '') form.name = name;
  for (const key of TIER_OUTCOME_PASSTHROUGH) {
    const value = o?.[key] ?? o?.outcome?.[key];
    if (value !== undefined && value !== null) form[key] = value;
  }

  if (form.points_count != null && form.count == null) form.count = form.points_count;
  return form;
};

const fromTierBlueprint = (tier: any): IReferralTier => {
  const display: IReferralTierDisplay = {};
  Object.keys(tier).forEach(key => {
    const match = WIRE_DISPLAY_PREFIXES.find(m => key.startsWith(m.wire));
    if (!match) return;
    const locale = key.slice(match.wire.length);
    if (!locale) return;
    const value = match.form === 'icon_url_' ? wrapIconForForm(tier[key]) : tier[key];
    if (value === undefined) return;
    (display as any)[`${match.form}${locale}`] = value;
  });
  // Legacy single icon_url field — also surface as display.icon_url so the
  // existing single-icon UI keeps rendering until per-locale icon inputs land.
  if (display.icon_url_en) display.icon_url = display.icon_url_en;
  else if (tier.tier_icon_url) display.icon_url = wrapIconForForm(tier.tier_icon_url);

  const transactionRule = tier.transaction_rule;
  const rules = transactionRule
    ? [
        {
          id: transactionRule.id,
          name: transactionRule.name,
          event_name: transactionRule.event_name,
          conditions: Array.isArray(transactionRule.conditions) ? transactionRule.conditions : []
        }
      ]
    : [];

  const outcomes = Array.isArray(tier.outcomes)
    ? tier.outcomes.map((o: any) => ({
        id: o.modularizable_id ?? o.outcome?.id ?? o.id,
        type: blueprintOutcomeTypeToWire(o.modularizable_type) ?? o.outcome?.type ?? o.type,
        label: o.label,
        modularizable_id: o.modularizable_id,
        modularizable_type: o.modularizable_type,
        outcome_raw: o.outcome
      }))
    : [];

  return {
    ...(tier.id !== undefined ? { id: tier.id } : {}),
    threshold: tier.required_referral_count,
    outcome: outcomes[0] ?? {},
    rules,
    display,
    prizes: []
  } as IReferralTier;
};

export const tierPayloadToForm = (data: any): any => {
  if (!data || data.tier_based_invite !== true) return data;

  const out = _cloneDeep(data);
  const tiers: any[] = Array.isArray(out.tiers) ? out.tiers : [];

  out.properties = out.properties ?? {};
  out.properties.reward_mode = REWARD_MODE_TIER_BASED;
  out.properties.tier_ladder = {
    ...(out.properties.tier_ladder ?? {}),
    tiers: tiers.map(fromTierBlueprint)
  };

  out.properties.tier_ladder.tiers.forEach((formTier: any, i: number) => {
    const wireTier = tiers[i];
    const wireOutcomes = Array.isArray(wireTier?.outcomes) ? wireTier.outcomes : [];
    formTier.outcomes = wireOutcomes.map(fromTierOutcome);
  });

  return out;
};
